import { type FC, useContext, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import ArrowTopIcon from '@assets/img/icons/arrow-top.svg?react';

import { type UseCheckRequirementsReturnType } from '@/common/hooks/check-requirements';
import { type PlantTypeText } from '@/common/types/plant';
import { type ResourceTypeText } from '@/common/types/resource';
import { ResourceItem } from '@/components/ResourceItem/ResourceItem';
import { StringXSmall } from '@/components/Typography/Typography';
import { StorageIndicator } from '@/components/ui/StorageIndicator/StorageIndicator';
import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';

import css from './styles.module.scss';

interface QuarryItemProps {
  isActive: boolean;
  id?: string;
  name: ResourceTypeText | PlantTypeText;
  level: number;
  upgradeRequirements: UseCheckRequirementsReturnType;
}

const QuarryItem: FC<QuarryItemProps> = ({
  isActive,
  id,
  name,
  level,
  upgradeRequirements,
}) => {
  const [ref, measure] = useMeasure();
  const { setActiveNavMeasure } = useContext(ClaimAnimationContext);

  useEffect(() => {
    if (isActive || id === name) {
      setActiveNavMeasure(measure);
    }
  }, [isActive, id, name, measure]);

  return (
    <ResourceItem
      ref={ref}
      className="relative"
      status={isActive || id === name ? 'active' : undefined}
      variant="light"
      radius="default"
      size={{ width: '5rem', height: '5rem' }}
      kind={name}
    >
      {level && (
        <span className={css.level}>
          <StringXSmall weight="bold" className="text-white/60">
            {level}
          </StringXSmall>
        </span>
      )}
      {upgradeRequirements.isComplete && (
        <ArrowTopIcon className={css.upgrade} />
      )}
      <StorageIndicator
        className="absolute right-[0.25rem] bottom-[0.25rem]"
        resource={name}
      />
    </ResourceItem>
  );
};

export { QuarryItem };
